import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LoadingState } from "@tabipla/ui";
import type { CreateExpenseDto } from "@tabipla/shared";
import { useGroupMembers } from "@/application/hooks/useGroups";
import { useDefaultSession } from "@/application/hooks/useDefaultSession";
import { useCreateExpense } from "@/application/hooks/useExpenses";
import { useAuth } from "@/application/hooks/useAuth";
import { ExpenseForm } from "./ExpenseForm";

interface QuickExpenseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
}

export function QuickExpenseDialog({ open, onOpenChange, groupId }: QuickExpenseDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Get members for the group
  const { data: members } = useGroupMembers(groupId);

  // Get/create default session for the group
  const { session, isLoading: sessionLoading } = useDefaultSession(groupId, true);

  // Create expense mutation
  const createExpenseMutation = useCreateExpense(groupId, session?.id ?? "");

  const handleExpenseSubmit = async (expense: CreateExpenseDto) => {
    try {
      await createExpenseMutation.mutateAsync(expense);
      toast.success(t("expenses.createSuccess", "経費を追加しました"));
      onOpenChange(false);
    } catch {
      toast.error(t("expenses.createError", "経費の追加に失敗しました"));
    }
  };

  // Show loading while fetching session
  if (sessionLoading || !session || !members || members.length === 0) {
    return (
      <LoadingState
        className={
          open ? "fixed inset-0 z-50 flex items-center justify-center bg-background/80" : "hidden"
        }
        message={t("sessions.loading", "読み込み中...")}
      />
    );
  }

  return (
    <ExpenseForm
      open={open}
      onOpenChange={onOpenChange}
      members={members}
      currentUserId={user?.id ?? ""}
      sessionId={session.id}
      onSubmit={handleExpenseSubmit}
      isSubmitting={createExpenseMutation.isPending}
    />
  );
}
